import { useState } from 'react';
import { useReduxState } from 'hooks';
import { Video } from 'components/common';
import { AreaProgressChart } from 'components/common/charts';
import { Dropdown } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';

import type { CategoricalChartFunc } from 'recharts/types/chart/types';
import type { GetVideoTimesRes } from 'shared-types/responses';

const videoControls = [
  'play-large',
  'play',
  'progress',
  'current-time',
  'mute',
  'volume'
];

interface Props {
  video: GetVideoTimesRes['videos'][0];
}

const VideosVideo = ({ video }: Props) => {
  const { messages, theme } = useReduxState();

  const [chartView, setChartView] = useState('views');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [jumpToTime, setJumpToTime] = useState(0);

  const updateCurrentTime = (player) => {
    setCurrentTime(player.currentTime);
  };

  const updateDuration = (player) => {
    setDuration(player.duration);
  };

  const updateChartView = (ev) => {
    const value = ev.target.getAttribute('data-value');
    setChartView(value);
  };

  const getTimeInSeconds = (time: string) => {
    const values = time.split(':').map((val) => parseInt(val.trim(), 10));
    return values[0] * 60 + values[1];
  };

  const getDurationTimeString = (totalSeconds: string | number) => {
    const time =
      typeof totalSeconds === 'string'
        ? parseInt(totalSeconds, 10)
        : totalSeconds;

    const durationDays = Math.floor(time / 86400000);

    const hours = Math.floor((time % (60 * 60 * 24)) / (60 * 60));
    const stringHrs = hours > 0 ? (hours < 10 ? '0' + hours : hours) + ':' : '';

    const minutes = Math.floor((time % (60 * 60)) / 60);
    const stringMins = (minutes < 10 ? '0' + minutes : minutes) + ':';

    const seconds = Math.floor(time % 60);
    const stringSecs = seconds < 10 ? '0' + seconds : seconds;

    const durationHourMinSec = `${stringHrs}${stringMins}${stringSecs}`;

    return `${
      durationDays > 0
        ? durationDays + ' ' + messages.commonDays.toLowerCase() + ' '
        : ''
    }${durationHourMinSec}`;
  };

  const handleChartClick: CategoricalChartFunc = (value) => {
    if (value && value.activeLabel) {
      const jumpToTime = getTimeInSeconds(`${value.activeLabel}`);
      setJumpToTime(jumpToTime);
    }
  };

  const viewsData = video.viewTimes
    ? Object.keys(video.viewTimes).map((time) => ({
        label: getDurationTimeString(time),
        [messages.analyticsVideoViews]: video.viewTimes[time]
      }))
    : null;

  const usersData = video.users
    ? Object.keys(video.users).map((time) => ({
        label: getDurationTimeString(time),
        [messages.analyticsVideoUsers]: video.users[time]
      }))
    : null;

  return (
    <div className="video">
      <div className="row-lg">
        <div className="col-1of2">
          <div className="video">
            {video.data.source ? (
              <Video
                controls={videoControls}
                jumpToTime={jumpToTime}
                onPlaying={updateDuration}
                onTimeupdate={updateCurrentTime}
                provider={video.data.source}
                videoId={video.data.remote_id}
                videoHash={video.data.remote_hash}
              />
            ) : video.data.file ? (
              <Video
                controls={videoControls}
                jumpToTime={jumpToTime}
                onPlaying={updateDuration}
                onTimeupdate={updateCurrentTime}
                poster={video.data.file.posterUrl}
                url={video.data.file.url}
              />
            ) : null}
          </div>
        </div>

        <div className="col-1of2 col-center">
          {viewsData ? (
            <>
              <div className="info">
                <div className="row row-center">
                  <div className="col">
                    <div className="info-text">
                      {chartView === 'views'
                        ? `${
                            messages.analyticsVideoTotalViewTime
                          }: ${getDurationTimeString(video.totalViewTime)}`
                        : `${messages.analyticsTotalUsers}: ${video.totalUsers}`}
                    </div>
                  </div>

                  <div className="col-min">
                    <Dropdown
                      category="lighter"
                      showArrow={true}
                      title={
                        chartView === 'views'
                          ? messages.analyticsVideoViews
                          : messages.analyticsVideoUsers
                      }
                    >
                      {[
                        {
                          label: messages.analyticsVideoViews,
                          value: 'views'
                        },
                        {
                          label: messages.analyticsVideoUsers,
                          value: 'users'
                        }
                      ].map((view, i) => (
                        <button
                          key={i}
                          data-value={view.value}
                          onClick={updateChartView}
                          style={{ position: 'relative' }}
                        >
                          {view.label}
                          {chartView === view.value ? (
                            <Icon
                              icon="check"
                              style={{
                                fontSize: '1rem',
                                position: 'absolute',
                                right: '12px',
                                top: '13px'
                              }}
                            />
                          ) : null}
                        </button>
                      ))}
                    </Dropdown>
                  </div>
                </div>
              </div>

              <div className="chart">
                <AreaProgressChart
                  id={video.id}
                  data={chartView === 'views' ? viewsData : usersData}
                  height={180}
                  onClick={handleChartClick}
                  progress={duration ? currentTime / duration : 0}
                  xAxisDataKey="label"
                  yAxisDataKey={
                    chartView === 'views'
                      ? messages.analyticsVideoViews
                      : messages.analyticsVideoUsers
                  }
                />
              </div>
            </>
          ) : (
            <div className="no-data">{messages.commonNoData}</div>
          )}
        </div>
      </div>

      <style jsx>{`
        .info {
          padding: 0 5px;
        }
        .info-text {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 700;
        }
        .no-data {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          font-weight: 400;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default VideosVideo;
